import type {ClientCallType} from '@pexip/infinity/clientCallType';

import {
    EXPRESS_PATH,
    MEETING_MANAGER_PATH,
    STEP_BY_STEP_PATH,
} from '../constants';
import type {VideoStreamTrackProcessorAPIs} from '../types';

import {toClientCallType} from './toClientCallType';

/** Join flow that a meeting link can request. */
export type MeetingLinkFlow =
    | typeof EXPRESS_PATH
    | typeof STEP_BY_STEP_PATH
    | typeof MEETING_MANAGER_PATH;

/** Participant role that a meeting link can request. */
export type MeetingLinkRole = 'host' | 'guest';

/**
 * Meeting link settings parsed from untrusted input. A field is absent when its
 * parameter was missing or invalid.
 */
export interface MeetingLinkParams {
    /** Conference alias to open. */
    conferenceAlias?: string;
    /** Display name of the joining participant. */
    displayName?: string;
    /** Host or Guest PIN, or `none` to join as a Guest without a PIN. */
    pin?: string;
    /** Requested participant role. */
    role?: MeetingLinkRole;
    /** Requested join flow. */
    flow?: MeetingLinkFlow;
    /** Whether to join without sending audio. */
    muteMicrophone?: boolean;
    /** Whether to join without sending video. */
    muteCamera?: boolean;
    /** Virtual Reception extension or Skype for Business Conference ID. */
    extension?: string;
    /** Maximum call bandwidth in kbps, as a canonical decimal string. */
    bandwidth?: string;
    /** Media directions allowed when joining. */
    callType?: ClientCallType;
    /** Whether the participant may change audio and video directions. */
    allowChangeOfMediaDirection?: boolean;
    /** Call tag included in logs, policy requests, and participant lists. */
    callTag?: string;
    /** Whether to join automatically without checking devices. */
    join?: boolean;
    /** Interface language tag. */
    language?: string;
    /** Opaque reference forwarded with the meeting route. */
    rid?: string;
    /** Video processing pipeline. */
    videoProcessingAPI?: VideoStreamTrackProcessorAPIs;
    /** Conference token. */
    token?: string;
    /** Single sign-on token. */
    ssoToken?: string;
}

/**
 * Query parameter name of every meeting link setting, as documented at
 * https://docs.pexip.com/apps/infinity_connect_url.htm.
 *
 * @example
 * new URLSearchParams({[MEETING_LINK_QUERY_NAMES.displayName]: 'Bob'});
 * // name=Bob
 */
export const MEETING_LINK_QUERY_NAMES = {
    conferenceAlias: 'conference',
    displayName: 'name',
    pin: 'pin',
    role: 'role',
    flow: 'flow',
    muteMicrophone: 'muteMicrophone',
    muteCamera: 'muteCamera',
    extension: 'extension',
    bandwidth: 'bandwidth',
    callType: 'callType',
    allowChangeOfMediaDirection: 'allowChangeOfMediaDirection',
    callTag: 'callTag',
    join: 'join',
    language: 'lng',
    rid: 'rid',
    videoProcessingAPI: 'videoProcessingAPI',
    token: 'token',
    ssoToken: 'ssoToken',
} as const satisfies Record<keyof MeetingLinkParams, string>;

/**
 * Query parameter that selects the meeting manager `flow` when `true`. It sets
 * {@link MeetingLinkParams.flow} rather than a field of its own.
 */
export const MEETING_MANAGER_QUERY_NAME = 'mm';

/** Query parameter name of a meeting link. */
export type MeetingLinkQueryName =
    | (typeof MEETING_LINK_QUERY_NAMES)[keyof typeof MEETING_LINK_QUERY_NAMES]
    | typeof MEETING_MANAGER_QUERY_NAME;

/** Options for {@link parseMeetingLinkParams}. */
export interface ParseMeetingLinkParamsOptions {
    /** Alias from the link path, used when there is no `conference` parameter. */
    pathConferenceAlias?: string;
    /** Flow from the link path, used when there is no `flow` or `mm` parameter. */
    pathFlow?: string;
}

// Documented range of the `bandwidth` link parameter:
// https://docs.pexip.com/apps/infinity_connect_url.htm
const MIN_BANDWIDTH_KBPS = 256;
const MAX_BANDWIDTH_KBPS = 6144;
const CONTROL_CHARACTER = /\p{Cc}/u;
const WHITESPACE = /\s/u;
const BANDWIDTH = /^\d{3,4}$/;
const LANGUAGE_TAG = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{1,8})*$/;

/**
 * Parses free text, rejecting empty and control-character input.
 *
 * @param value - Raw parameter value.
 * @returns The trimmed text, or `undefined` when it is invalid.
 *
 * @example
 * parseText(' Bob '); // 'Bob'
 */
const parseText = (value: string | null | undefined): string | undefined => {
    const text = value?.trim();
    if (!text || CONTROL_CHARACTER.test(text)) {
        return undefined;
    }
    return text;
};

/**
 * Parses an opaque credential, which never contains whitespace.
 *
 * @param value - Raw parameter value.
 * @returns The credential, or `undefined` when it is invalid.
 *
 * @example
 * parseToken('abc.def'); // 'abc.def'
 */
const parseToken = (value: string | null): string | undefined => {
    const token = parseText(value);
    return token && !WHITESPACE.test(token) ? token : undefined;
};

/**
 * Decodes a display name a second time, as earlier Infinity Connect versions
 * did, and keeps the value unchanged when it is not valid percent-encoding.
 *
 * @param value - Raw parameter value.
 * @returns The display name, or `undefined` when it is invalid.
 *
 * @example
 * parseDisplayName('Bob%20Smith'); // 'Bob Smith'
 */
const parseDisplayName = (value: string | null): string | undefined => {
    if (value === null) {
        return undefined;
    }
    try {
        return parseText(decodeURIComponent(value.replace(/\+/g, ' ')));
    } catch {
        return parseText(value);
    }
};

/**
 * Parses a case-insensitive `true` or `false`.
 *
 * @example
 * parseBoolean('TRUE'); // true
 */
const parseBoolean = (value: string | null): boolean | undefined => {
    switch (value?.toLowerCase()) {
        case 'true':
            return true;
        case 'false':
            return false;
        default:
            return undefined;
    }
};

/**
 * Parses a bandwidth within the documented range of 256 to 6144 kbps.
 *
 * @example
 * parseBandwidth('1264'); // '1264'
 */
const parseBandwidth = (value: string | null): string | undefined => {
    if (!value || !BANDWIDTH.test(value)) {
        return undefined;
    }
    const kbps = Number(value);
    return kbps >= MIN_BANDWIDTH_KBPS && kbps <= MAX_BANDWIDTH_KBPS
        ? String(kbps)
        : undefined;
};

/**
 * Parses a join flow name.
 *
 * @example
 * parseFlow('express'); // 'express'
 */
const parseFlow = (
    value: string | null | undefined,
): MeetingLinkFlow | undefined => {
    switch (value) {
        case EXPRESS_PATH:
        case STEP_BY_STEP_PATH:
        case MEETING_MANAGER_PATH:
            return value;
        default:
            return undefined;
    }
};

/**
 * Parses a participant role.
 *
 * @example
 * parseRole('guest'); // 'guest'
 */
const parseRole = (value: string | null): MeetingLinkRole | undefined => {
    switch (value) {
        case 'host':
        case 'guest':
            return value;
        default:
            return undefined;
    }
};

/**
 * Parses a video processing pipeline name, ignoring case.
 *
 * @example
 * parseVideoProcessingAPI('Stream'); // 'stream'
 */
const parseVideoProcessingAPI = (
    value: string | null,
): VideoStreamTrackProcessorAPIs | undefined => {
    const name = value?.toLowerCase();
    switch (name) {
        case 'stream':
        case 'canvas':
            return name;
        default:
            return undefined;
    }
};

/**
 * Parses a value that must match a pattern in full.
 *
 * @example
 * parsePattern('en-US', LANGUAGE_TAG); // 'en-US'
 */
const parsePattern = (
    value: string | null,
    pattern: RegExp,
): string | undefined => (value && pattern.test(value) ? value : undefined);

/**
 * Parses meeting link query parameters into settings whose values are known to
 * be well formed. Invalid values are dropped individually so that one bad
 * parameter does not discard the rest of the link.
 *
 * @param query - Query parameters of the link.
 * @param options - Values from the link path.
 * @returns The parsed settings.
 *
 * @example
 * parseMeetingLinkParams(new URLSearchParams('conference=meet.alice&pin=1234'));
 * // {conferenceAlias: 'meet.alice', pin: '1234'}
 */
export const parseMeetingLinkParams = (
    query: URLSearchParams,
    {pathConferenceAlias, pathFlow}: ParseMeetingLinkParamsOptions = {},
): MeetingLinkParams => {
    const get = (field: keyof MeetingLinkParams) =>
        query.get(MEETING_LINK_QUERY_NAMES[field]);
    const callType = get('callType');
    const allowChangeOfMediaDirection = get('allowChangeOfMediaDirection');

    return {
        conferenceAlias: parseText(
            get('conferenceAlias') ?? pathConferenceAlias,
        ),
        displayName: parseDisplayName(get('displayName')),
        pin: parseText(get('pin')),
        role: parseRole(get('role')),
        flow:
            query.get(MEETING_MANAGER_QUERY_NAME) === 'true'
                ? MEETING_MANAGER_PATH
                : parseFlow(get('flow') ?? pathFlow),
        muteMicrophone: parseBoolean(get('muteMicrophone')),
        muteCamera: parseBoolean(get('muteCamera')),
        extension: parseText(get('extension')),
        bandwidth: parseBandwidth(get('bandwidth')),
        callType: callType ? toClientCallType(callType) : undefined,
        allowChangeOfMediaDirection: allowChangeOfMediaDirection
            ? allowChangeOfMediaDirection === 'true'
            : undefined,
        callTag: parseText(get('callTag')),
        join: get('join') === '1' ? true : undefined,
        language: parsePattern(get('language'), LANGUAGE_TAG),
        rid: parseText(get('rid')),
        videoProcessingAPI: parseVideoProcessingAPI(get('videoProcessingAPI')),
        token: parseToken(get('token')),
        ssoToken: parseToken(get('ssoToken')),
    };
};
